import React, { useEffect, useMemo, useState } from "react";
import { api, fmtQty } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Ship, PackageCheck, PackageX, ShoppingBag, FileClock, Clock } from "lucide-react";
import { DataTable } from "@/components/common/DataTable";
import { ReadyBadge, d10 } from "@/components/common/cells";

const TONES = {
  default: "bg-white border-slate-200",
  success: "bg-emerald-50 border-emerald-200",
  warning: "bg-amber-50 border-amber-200",
  danger: "bg-red-50 border-red-200",
  info: "bg-sky-50 border-sky-200",
};

const KpiCard = ({ icon: Icon, label, value, tone = "default", testid }) => (
  <div className={`rounded-lg border p-5 ${TONES[tone]}`} data-testid={testid}>
    <div className="flex items-center justify-between mb-3">
      <span className="text-xs uppercase tracking-wider font-semibold text-slate-500">{label}</span>
      <Icon size={18} className="text-slate-500" />
    </div>
    <div className="text-2xl font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>{value}</div>
  </div>
);

export default function Dashboard() {
  const { t } = useLang();
  const { user } = useAuth();
  const [kpi, setKpi] = useState(null);
  useEffect(() => { api.get("/reports/dashboard").then((r) => setKpi(r.data)); }, []);

  const columns = useMemo(() => [
    { key: "plan_code", label: t("plan_code"), className: "font-mono text-xs" },
    { key: "buyer", label: t("buyers") },
    { key: "product", label: t("products") },
    { key: "destination_country", label: t("destination_country") },
    { key: "qty", label: t("qty"), align: "right", className: "font-mono", render: (r) => fmtQty(r.qty) },
    { key: "etd", label: t("etd"), className: "font-mono text-xs whitespace-nowrap", value: (r) => d10(r.etd) },
    { key: "vessel", label: t("vessel") },
    { key: "ready", label: t("status"), align: "center", value: (r) => (r.ready ? t("ready") : t("pending")), render: (r) => <ReadyBadge ready={r.ready} t={t} /> },
  ], [t]);

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>{t("dashboard")}</h1>
        <p className="text-sm text-slate-500 mt-1">{t("hello")}, {user?.name}. {t("welcome")}.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <KpiCard icon={Ship} label={t("kpi_total")} value={kpi?.total_shipments ?? "-"} testid="kpi-total" />
        <KpiCard icon={Clock} label={t("kpi_active")} value={kpi?.active_shipments ?? "-"} tone="info" testid="kpi-active" />
        <KpiCard icon={PackageCheck} label={t("kpi_ready")} value={kpi?.ready_shipments ?? "-"} tone="success" testid="kpi-ready" />
        <KpiCard icon={PackageX} label={t("kpi_pending")} value={kpi?.pending_shipments ?? "-"} tone="warning" testid="kpi-pending" />
        <KpiCard icon={ShoppingBag} label={t("kpi_to_buy")} value={kpi?.items_to_buy ?? "-"} tone="danger" testid="kpi-to-buy" />
        <KpiCard icon={FileClock} label={t("kpi_open_po")} value={kpi?.open_pos ?? "-"} testid="kpi-open-po" />
      </div>
      <Card className="border-slate-200">
        <CardContent className="p-0">
          <div className="p-4 border-b border-slate-200">
            <h3 className="text-lg font-semibold" style={{ fontFamily: "'Outfit', sans-serif" }}>{t("upcoming_shipments")}</h3>
          </div>
          <DataTable columns={columns} rows={kpi?.upcoming || []} testid="upcoming-shipments-table" maxHeight="45vh" />
        </CardContent>
      </Card>
    </div>
  );
}
